import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { Route, useHistory } from 'react-router';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import LINKS from '../../../../configs/links';
import { extractFromArray, parseQuery, stringifyQuery } from '../../../../utils';
import Button from '../../../common/base/Button';
import Icon from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import DropdownMenu from '../../../common/DropdownMenu';
import { Div } from '../../../common/helpers/StyledUtils';
import Search from '../../../common/Search';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { withTeamPermission } from '../../../contexts/TeamPermissionContext';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary } from '../../../layouts/ViewLayout';
import PostCreateModal from '../Connections/Create/PostCreateModal';
import { rules_default } from '../Connections/Forms/rules';
import TransformRuleFormWrapper from '../Connections/Forms/TransformRuleForm';
import TransformationsList from './TransformationsList';
const sort_options = {
  created_at_desc: {
    label: 'Newest -> Oldest',
    dir: 'desc',
    key: 'created_at'
  },
  created_at_asc: {
    label: 'Oldest -> Newest',
    dir: 'asc',
    key: 'created_at'
  },
  name_asc: {
    label: 'A -> Z',
    dir: 'asc',
    key: 'name'
  },
  name_desc: {
    label: 'Z -> A',
    dir: 'desc',
    key: 'name'
  },
  updated_at: {
    label: 'Last updated',
    dir: 'desc',
    key: 'updated_at'
  }
} as const;
const sort_keys = Object.keys(sort_options) as (keyof typeof sort_options)[];
const DEFAULT_PAGE_SIZE = 50;
export interface TransformationsListQueryParams {
  selected_transformation_id?: string;

  // Pagination & sorting
  order_by?: keyof typeof sort_options;
  next?: string[];
  prev?: string[];

  // Filters
  search_term?: string;
}
interface TransformationsListFiltersProps {
  id?: string;
  name?: {
    contains: string;
  };
  // Pagination & sorting
  order_by?: (typeof sort_options)[keyof typeof sort_options]['key'];
  dir?: 'asc' | 'desc';
  next?: string;
  prev?: string;
  limit?: number;
}
const extractFiltersFromQuery = (parsed_query: TransformationsListQueryParams): TransformationsListFiltersProps => {
  let name: {
    contains: string;
  } | undefined = undefined;
  let id: string | undefined = undefined;
  if (parsed_query.search_term) {
    if (parsed_query.search_term.startsWith('tf_')) {
      id = parsed_query.search_term.trim();
    } else {
      name = {
        contains: parsed_query.search_term
      };
    }
  }
  return {
    id,
    name,
    order_by: parsed_query.order_by ? sort_options[parsed_query.order_by]?.key : undefined,
    dir: parsed_query.order_by ? sort_options[parsed_query.order_by]?.dir : undefined,
    next: extractFromArray(parsed_query.next || []) as string,
    prev: extractFromArray(parsed_query.prev || []) as string,
    limit: DEFAULT_PAGE_SIZE
  };
};
const CreateTransformationModal: React.FC<{
  mutateTransformations: () => void;
}> = t0 => {
  const $ = _c(9);
  const {
    mutateTransformations
  } = t0;
  const history = useHistory();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  let t1;
  if ($[0] !== HookdeckAPI.transformations || $[1] !== addToast || $[2] !== mutateTransformations) {
    t1 = v => HookdeckAPI.transformations.create({
      ...v.transformation,
      env: v.transformation.env?.reduce(_temp, {})
    }).then(() => {
      mutateTransformations();
      addToast("success", "New transformation created");
    }).catch(e => {
      if (e.response?.code === "RESOURCE_ALREADY_EXISTS") {
        addToast("error", `A transformation named "${v.transformation.name}" already exists.`);
      } else {
        addToast("error", `Failed to create transformation${e.response?.data?.[0] ? `: ${e.response.data[0]}` : ""}`);
      }
      throw e;
    });
    $[0] = HookdeckAPI.transformations;
    $[1] = addToast;
    $[2] = mutateTransformations;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onSave = t1;
  let t2;
  if ($[4] !== history) {
    t2 = () => {
      history.push("/transformations");
    };
    $[4] = history;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onSave || $[7] !== t2) {
    t3 = <TransformRuleFormWrapper onSave={onSave} transformation={rules_default.transform.transformation} promptOnClose={true} onClose={t2} />;
    $[6] = onSave;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const TransformationsView: React.FC = () => {
  const $ = _c(60);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      order_by: "created_at_desc"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    query,
    updateSearchQuery
  } = useSearchQuery(t0);
  let t1;
  if ($[1] !== query || $[2] !== updateSearchQuery) {
    t1 = term => {
      updateSearchQuery({
        ...query,
        search_term: term || undefined
      });
    };
    $[1] = query;
    $[2] = updateSearchQuery;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const setSearchTerm = t1;
  let filters;
  let page_size;
  let t2;
  let t3;
  if ($[4] !== query) {
    filters = extractFiltersFromQuery(query);
    page_size = filters.limit || DEFAULT_PAGE_SIZE;
    t2 = useSWR;
    t3 = APIMethodKeys.transformations.list(filters);
    $[4] = query;
    $[5] = filters;
    $[6] = page_size;
    $[7] = t2;
    $[8] = t3;
  } else {
    filters = $[5];
    page_size = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== HookdeckAPI.transformations || $[10] !== filters) {
    t4 = () => HookdeckAPI.transformations.list(filters);
    $[9] = HookdeckAPI.transformations;
    $[10] = filters;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      keepPreviousData: true,
      dedupingInterval: 150
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    data,
    mutate,
    isLoading
  } = t2(t3, t4, t5);
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = APIMethodKeys.transformations.count({});
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== HookdeckAPI.transformations) {
    t7 = () => HookdeckAPI.transformations.count({});
    $[14] = HookdeckAPI.transformations;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    data: count_data
  } = useSWR(t6, t7);
  let t8;
  if ($[16] !== query.created) {
    t8 = query.created && <PostCreateModal />;
    $[16] = query.created;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      y: 5
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "center",
      justify: "space-between",
      gap: 2
    };
    t11 = <Text semi={true} ellipsis={true}>Transformations</Text>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Div flex={t10}>{t11}<Tooltip tooltip="Transformations are a Hookdeck rule that let you modify the payload of an event prior to delivery. Unlike other rules, they can be shared across connections." side="bottom" max_width={380} cta={{
        label: "Transformations docs",
        to: LINKS.product_docs.transformations
      }}><Icon icon="info" muted={true} /></Tooltip></Div>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <StyledViewNavHeader p={t9}>{t12}<StyledViewNavActions><Button.Permission to={_temp2} primary={true} icon="add_circle">New transformation</Button.Permission></StyledViewNavActions></StyledViewNavHeader>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      b: 3
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      align: "center",
      justify: "space-between",
      gap: 3
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      px: 300
    };
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = query.search_term || "";
  let t18;
  if ($[26] !== setSearchTerm || $[27] !== t17) {
    t18 = <Div w={t16}><Search onChange={setSearchTerm} value={t17} placeholder="Filter by name" small={true} /></Div>;
    $[26] = setSearchTerm;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== query.order_by || $[30] !== updateSearchQuery) {
    t19 = sort_keys.map(key => ({
      onClick: () => updateSearchQuery({
        order_by: key
      }),
      label: sort_options[key]?.label,
      selected: query.order_by === key
    }));
    $[29] = query.order_by;
    $[30] = updateSearchQuery;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      min: 150
    };
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const t21 = sort_options[query.order_by || "name"]?.label;
  let t22;
  if ($[33] !== t19 || $[34] !== t21) {
    t22 = <DropdownMenu options={t19} p={0} parent_width={t20} placement="bottom-end" label_prefix="Order by" label={t21} expand_icon="chevron_down" icon="sort" outline={true} />;
    $[33] = t19;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t18 || $[37] !== t22) {
    t23 = <StyledViewNav>{t13}<StyledViewNavSecondary p={t14}><Div w={100} flex={t15}>{t18}{t22}</Div></StyledViewNavSecondary></StyledViewNav>;
    $[36] = t18;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== data?.models) {
    t24 = data?.models || [];
    $[39] = data?.models;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  const t25 = count_data?.count || 0;
  const t26 = data?.pagination;
  let t27;
  if ($[41] !== page_size || $[42] !== t26) {
    t27 = {
      ...t26,
      page_size
    };
    $[41] = page_size;
    $[42] = t26;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  const t28 = !!query.search_term;
  let t29;
  if ($[44] !== isLoading || $[45] !== mutate || $[46] !== t24 || $[47] !== t25 || $[48] !== t27 || $[49] !== t28) {
    t29 = <TransformationsList transformations={t24} total={t25} loading={isLoading} pagination={t27} mutate={mutate} filtering={t28} />;
    $[44] = isLoading;
    $[45] = mutate;
    $[46] = t24;
    $[47] = t25;
    $[48] = t27;
    $[49] = t28;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== mutate) {
    t30 = <Route exact={true} path="/transformations/new" render={() => <CreateTransformationModal mutateTransformations={mutate} />} />;
    $[51] = mutate;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t23 || $[54] !== t29 || $[55] !== t30) {
    t31 = <StyledViewLayout><StyledViewContent>{t23}{t29}{t30}</StyledViewContent></StyledViewLayout>;
    $[53] = t23;
    $[54] = t29;
    $[55] = t30;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== t31 || $[58] !== t8) {
    t32 = <>{t8}{t31}</>;
    $[57] = t31;
    $[58] = t8;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  return t32;
};
export default withTeamPermission(TransformationsView, 'member');
function _temp(object, t0) {
  const [key, value] = t0;
  return {
    ...object,
    [key]: value
  };
}
function _temp2(location) {
  return {
    ...location,
    pathname: "/transformations/new",
    search: `?${stringifyQuery({
      ...parseQuery(location.search),
      default_transformation_id: undefined
    })}`,
    state: {
      scroll: false
    }
  };
}